import React, { useEffect, useMemo } from "react";
import { change, Field, getFormValues } from "redux-form";
import { useDispatch, useSelector } from "react-redux";
import { ReduxControls } from "rhombus-react";

import { ASSET_TYPES, CREATIVE_ASSET_TYPES, SOCIAL_AD_TYPES } from "utils/constants";
import { formatBytes } from "utils/common";
import { getCardIndexFromName, isRequiredAssetField } from "social/utils";
import DynamicInput from "social/DynamicInput";
import AssetSelect from "social/AssetSelect";
import DynamicAssetSelect from "social/DynamicAssetSelect";
import { SOCIAL_WIZARD } from "widgets/forms/list";
import {
	FACEBOOK_IMAGE_CONSTRAINTS,
	FACEBOOK_VIDEO_CONSTRAINTS
} from "social/validators/facebook";

import {
	AE_CARD_IMAGE_DISABLED_MESSAGE,
	UNEDITABLE_CARD_ASSET_TYPE_MESSAGE,
	UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE
} from "social/constants/common";
import { imposiumCompositionForCard } from "social/ImposiumCompSelector";
import { afterEffectsPackage } from "social/AfterEffectsPackageSelector";
import { afterEffectsCompositionForCard } from "social/AfterEffectsCompSelector";
import { imposiumProject } from "social/ImposiumProjectSelector";

import "./CarouselCardForm.css";

const {
	MAX_LENGTH_LANDING_PAGE,
	MAX_IMAGE_BYTES,
	IMAGE_ACCEPT,
} = FACEBOOK_IMAGE_CONSTRAINTS;

const {
	MAX_VIDEO_BYTES,
	VIDEO_ACCEPT,
	MIN_VIDEO_SEC,
	MAX_VIDEO_MIN,
} = FACEBOOK_VIDEO_CONSTRAINTS;


const FACEBOOK_IMAGE_REQUIREMENTS = [
	{
		name: "File Size:",
		description: `${formatBytes(MAX_IMAGE_BYTES)} Maximum`,
	},
	{
		name: "File Types:",
		description: IMAGE_ACCEPT,
	},
	{
		name: "Dimensions:",
		description: "254x133 pixels or larger required, 1080x1080 or larger recommended",
	}
];

const FACEBOOK_THUMBNAIL_REQUIREMENTS = [
	{
		name: "File Size:",
		description: `${formatBytes(MAX_IMAGE_BYTES)} Maximum`,
	},
	{
		name: "File Types:",
		description: IMAGE_ACCEPT,
	},
];

const FACEBOOK_VIDEO_REQUIREMENTS = [
	{
		name: "File Size:",
		description: `${formatBytes(MAX_VIDEO_BYTES)} Maximum`,
	},
	{
		name: "File Types:",
		description: VIDEO_ACCEPT,
	},
	{
		name: "Dimensions:",
		description: "120x120 pixels or larger required, 1080x1080 or larger recommended",
	},
	{
		name: "Duration:",
		description: `Between ${MIN_VIDEO_SEC} sec and ${MAX_VIDEO_MIN} min`,
	}
];

export const FACEBOOK_CAROUSEL_FIELDS = {
	video: {
		name: "video",
		label: "Video Asset",
		className: "full-width cypress-video-select",
		assetType: ASSET_TYPES.VIDEO,
		component: AssetSelect,
		requirements: FACEBOOK_VIDEO_REQUIREMENTS,
		required: true,
		accept: VIDEO_ACCEPT,
	},
	"video thumbnail": {
		name: "image",
		label: "Video Thumbnail",
		className: "full-width cypress-video-thumbnail",
		assetType: ASSET_TYPES.IMAGE,
		component: AssetSelect,
		requirements: FACEBOOK_THUMBNAIL_REQUIREMENTS,
		required: true,
		accept: IMAGE_ACCEPT,
	},
	image: {
		name: "image",
		label: "Image Asset",
		className: "full-width cypress-image-select",
		assetType: ASSET_TYPES.IMAGE,
		component: AssetSelect,
		requirements: FACEBOOK_IMAGE_REQUIREMENTS,
		required: true,
		accept: IMAGE_ACCEPT,
	},
	headline: {
		name: "headline",
		label: "Headline",
		className: "full-width cypress-headline",
		placeholder: "Enter a short headline",
		tip: {
			description: "May get truncated after 40 characters",
			direction: "right",
		},
		component: ReduxControls.Input,
	},
	description: {
		name: "description",
		label: "Description",
		className: "full-width cypress-description",
		placeholder: "Include additional details",
		tip: {
			description: "May get truncated after 20 characters",
			direction: "right",
		},
		component: ReduxControls.Input,
	},
	landingPage: {
		name: "landingPage",
		label: "Landing Page",
		className: "full-width cypress-landing-page",
		placeholder: "https://www.example.com",
		component: ReduxControls.Input,
		charMax: MAX_LENGTH_LANDING_PAGE,
		showCharCount: true,
		required: true,
	},
};

export const FACEBOOK_CAROUSEL_MO_FIELDS = {
	imposiumProject,
	imposiumComposition: imposiumCompositionForCard,
	afterEffectsPackage,
	afterEffectsComposition: afterEffectsCompositionForCard,
	video: {
		name: "video",
		label: "Video Asset",
		className: "full-width cypress-video-select",
		assetType: ASSET_TYPES.VIDEO,
		component: DynamicAssetSelect,
		defaultDynVarName: ({ name }) => `card${getCardIndexFromName(name)}_product`,
		requirements: FACEBOOK_VIDEO_REQUIREMENTS,
		required: true,
		accept: VIDEO_ACCEPT,
	},
	"video thumbnail": {
		name: "image",
		label: "Video Thumbnail",
		className: "full-width cypress-video-thumbnail",
		assetType: ASSET_TYPES.IMAGE,
		component: DynamicAssetSelect,
		defaultDynVarName: ({ name }) => `card${getCardIndexFromName(name)}_video_thumbnail`,
		requirements: FACEBOOK_THUMBNAIL_REQUIREMENTS,
		required: true,
		accept: IMAGE_ACCEPT,
	},
	image: {
		name: "image",
		label: "Image Asset",
		className: "full-width cypress-image-select",
		assetType: ASSET_TYPES.IMAGE,
		component: DynamicAssetSelect,
		defaultDynVarName: ({ name }) => `card${getCardIndexFromName(name)}_product`,
		requirements: FACEBOOK_IMAGE_REQUIREMENTS,
		required: true,
		accept: IMAGE_ACCEPT,
	},
	headline: {
		name: "headline",
		label: "Headline",
		className: "full-width cypress-headline",
		placeholder: "Enter a short headline",
		tip: {
			description: "May get truncated after 40 characters",
			direction: "right",
		},
		component: DynamicInput,
		defaultDynVarName: ({ name }) => `card${getCardIndexFromName(name)}_headline`,
	},
	description: {
		name: "description",
		label: "Description",
		className: "full-width cypress-description",
		placeholder: "Include additional details",
		tip: {
			description: "May get truncated after 20 characters",
			direction: "right",
		},
		component: DynamicInput,
		defaultDynVarName: ({ name }) => `card${getCardIndexFromName(name)}_description`,
	},
	landingPage: {
		name: "landingPage",
		label: "Landing Page",
		className: "full-width cypress-landing-page",
		placeholder: "https://www.example.com",
		component: DynamicInput,
		defaultDynVarName: ({ name }) => `card${getCardIndexFromName(name)}_clickTag1`,
		charMax: MAX_LENGTH_LANDING_PAGE,
		required: true,
	},
};

const FIELDS_BY_AD_TYPE = {
	[SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL]: FACEBOOK_CAROUSEL_FIELDS,
	[SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO]: FACEBOOK_CAROUSEL_MO_FIELDS,
};

const VIDEO_ONLY_FIELDS = [
	"afterEffectsComposition",
	"afterEffectsPackage",
	"frameNumber",
];

function fieldGenerator(props) {
	const {
		adType,
		assetType,
		creativeAssetType,
		libraryId,
		cardPrefix,
		navigationActions,
		isViewOnly = false,
		showPristineErrors = false,
		doVersionsExist,
	} = props;


	return (name) => {
		const fieldProps = FIELDS_BY_AD_TYPE[adType][name];

		let extraProps = {
			key: name,
			disabled: isViewOnly,
			showPristineErrors,
		};

		if(extraProps.disabled && fieldProps.placeholder){
			// We set this on extraProps, because we don't want to mutate the fieldProps consts
			extraProps.placeholder = "";
		}

		// If versions exist besides default, then dynamic toggle should be disabled
		if (doVersionsExist) {
			extraProps.dynamicToggleDisabled = true;
			extraProps.variableNameInputDisabled = true;
			extraProps.variableNameInputTip = UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE;
		}

		if(/image|video/gi.test(fieldProps.name)) {
			extraProps.libraryId = libraryId;
			extraProps.navigationActions = navigationActions;
			extraProps.creativeAssetType = creativeAssetType;
			extraProps.required = isRequiredAssetField(creativeAssetType);
		}

		if(assetType === "image" && fieldProps.label?.includes("Video")){
			return;
		} else if(assetType === "video" && fieldProps.label === "Image Asset") {
			/*
				Both an image type card and a video type card have fields named "image" for Facebook Carousels.
				We compare the label to cut the "Image Asset" version from the video type form.
			*/
			return;
		}

		if (assetType === "image" && VIDEO_ONLY_FIELDS.includes(fieldProps.name)) {
			return;
		}

		if(cardPrefix === "cards[0]" && name === "imposiumComposition"){
			extraProps.showProjectName = false;
		}

		return (
			<Field
				key={fieldProps.label}
				{...fieldProps}
				{...extraProps}
				name = {`${cardPrefix}.${fieldProps.name}`}
			/>
		);
	};
}

export default function FacebookCarouselCardForm(props) {
	const {
		libraryId,
		navigationActions,
		adType,
		isViewOnly,
		isEditMode,
		showPristineErrors,
		doVersionsExist,
	} = props;

	const dispatch = useDispatch();

	const values = useSelector(state => getFormValues(SOCIAL_WIZARD)(state)?.[adType] || {});

	const currentCard = values?.currentCard || 0;
	const assetType = values?.cards?.[currentCard]?.type;
	const creativeAssetType = values.assetType;
	const cardPrefix = `cards[${currentCard}]`;
	const typeFieldName = `${cardPrefix}.type`;

	useEffect(() => {
		if(assetType === "image" && creativeAssetType === CREATIVE_ASSET_TYPES.AFTER_EFFECTS){
			dispatch(change(SOCIAL_WIZARD, `${adType}.${cardPrefix}.type`, "video"));
		}
	}, [assetType, creativeAssetType]);

	const generateFields = fieldGenerator({
		adType,
		assetType,
		creativeAssetType,
		libraryId,
		cardPrefix,
		isViewOnly,
		isEditMode,
		showPristineErrors,
		navigationActions,
		doVersionsExist,
	});
	const fields = useMemo(() => Object.keys(FIELDS_BY_AD_TYPE[adType]).map(generateFields), [assetType, creativeAssetType, currentCard]);
	const aeAssetTypeMessage = creativeAssetType === CREATIVE_ASSET_TYPES.AFTER_EFFECTS ? AE_CARD_IMAGE_DISABLED_MESSAGE : "";

	return (
		<div className="carousel-card-form">
			<div className="fields">
				<div className="asset-type-radio-container">
					<span className="asset-type-heading">Asset Type:</span>
					<div data-tip={doVersionsExist ? UNEDITABLE_CARD_ASSET_TYPE_MESSAGE : aeAssetTypeMessage }>
						<Field
							label="Image"
							type="radio"
							className="asset-type-radio cypress-image-radio"
							name={typeFieldName}
							value={"image"}
							component={ReduxControls.Checkbox}
							checked={assetType === "image"}
							disabled={isViewOnly || doVersionsExist || creativeAssetType === CREATIVE_ASSET_TYPES.AFTER_EFFECTS}
						/>
					</div>
					<div data-tip={doVersionsExist ? UNEDITABLE_CARD_ASSET_TYPE_MESSAGE : "" }>
						<Field
							label="Video"
							type="radio"
							className="asset-type-radio cypress-video-radio"
							name={typeFieldName}
							value={"video"}
							component={ReduxControls.Checkbox}
							checked={assetType === "video"}
							disabled={isViewOnly || doVersionsExist}
						/>
					</div>
				</div>
				{fields}
			</div>
		</div>
	);
}
